import { Client } from "@microsoft/microsoft-graph-client";
import { AuthenticationProvider } from "@microsoft/microsoft-graph-client";
import { sharepointConfig } from "../auth/authConfig";

// Define the FormData interface here to avoid import issues
interface FormData {
  name: string;
  email: string;
  phone: string;
  referredBy: string;
  consentToContact: boolean;
}

// Create an authentication provider that uses the MSAL instance to acquire tokens
class MsalAuthProvider implements AuthenticationProvider {
  private tokenFunction: () => Promise<string>;

  constructor(getTokenFunction: () => Promise<string>) {
    this.tokenFunction = getTokenFunction;
  }

  /**
   * This method will get called before every request to the Microsoft Graph API
   * It should return a Promise that resolves to an access token or rejects with an error
   */
  public async getAccessToken(): Promise<string> {
    return await this.tokenFunction();
  }
}

/**
 * Creates a Microsoft Graph client with the provided token acquisition function
 */
export const createGraphClient = (getAccessToken: () => Promise<string>) => {
  // Initialize the auth provider
  const authProvider = new MsalAuthProvider(getAccessToken);

  // Initialize the Graph client
  return Client.initWithMiddleware({
    authProvider,
  });
};

/**
 * Adds a new item to the SharePoint list
 * @param graphClient - The Microsoft Graph client
 * @param data - The form data to add to SharePoint
 * @returns A promise that resolves when the item is added
 */
export const addItemToSharePointList = async (
  graphClient: Client,
  data: FormData & { platformType?: string }
): Promise<boolean> => {
  try {
    // Extract site name from the full URL
    const siteUrl = sharepointConfig.siteUrl;
    if (!siteUrl) {
      // Removed console.error
      return false;
    }

    // Removed console.log
    
    // For direct submission to SharePoint, we'll use a simpler approach
    // Extract the domain from the URL (e.g., invyteout.sharepoint.com)
    const domain = new URL(siteUrl).hostname;
    
    // Extract the site name from the URL path
    const urlPath = new URL(siteUrl).pathname;
    const siteName = urlPath.split('/').filter(Boolean)[1]; // e.g., InvyteOut
    
    // Log the extracted information for debugging
    // Removed console.log
    
    if (!siteName) {
      // Removed console.error
      return false;
    }
    
    // Removed console.log
    
    // In Microsoft Graph API, we can reference a SharePoint site in several ways:
    // 1. By site ID
    // 2. By server-relative URL
    // 3. By host name + server-relative URL
    // We'll try different approaches to find the right format

    // Determine which list to use based on platformType
    const listName = data.platformType?.toLowerCase() === 'invyteout' 
      ? sharepointConfig.invyteout.listName 
      : sharepointConfig.vynterface.listName;

    // Removed console.log
    
    // Prepare the item to add to the list
    // First, let's log the data we're trying to submit
    // Removed console.log
    
    // Create a more flexible structure that tries different field name formats
    const listItem = {
      fields: {
        // Common field names - Title is required by SharePoint
        Title: data.name || 'New Waitlist Entry',
        
        // Try different variations of field names
        // For Name
        Name: data.name,
        name: data.name,
        
        // For Email
        Email: data.email,
        email: data.email,
        EmailAddress: data.email,
        
        // For Phone - try both string and number formats
        Phone: data.phone || '',
        phone: data.phone || '',
        PhoneNumber: data.phone || '',
        phoneNumber: data.phone || '',
        "Phone Number": data.phone || '',
        
        // For Referred By
        ReferredBy: data.referredBy || '',
        referredBy: data.referredBy || '',
        "Referred By": data.referredBy || '',
        Referrer: data.referredBy || '',
        referrer: data.referredBy || '',
        
        // For Consent to Contact
        ConsentToContact: data.consentToContact || false,
        consentToContact: data.consentToContact || false,
        OptIn: data.consentToContact || false,
        optIn: data.consentToContact || false,
        Consent: data.consentToContact || false,
        consent: data.consentToContact || false,
        
        // Include platform type
        Platform: data.platformType || 'Vynterface',
        platform: data.platformType || 'Vynterface',
        PlatformType: data.platformType || 'Vynterface',
        platformType: data.platformType || 'Vynterface',
      },
    };

    try {
      // Try a direct approach using the domain and site path
      const domain = new URL(siteUrl).hostname;
      const sitePath = new URL(siteUrl).pathname;
      
      // Removed console.log
      
      // Define an interface for the SharePoint list item
      interface SharePointList {
        id: string;
        name: string;
        displayName: string;
      }
      
      // Try a different approach to get the site
      try {
        // First try using the domain:path format
        const siteResponse = await graphClient
          .api(`/sites/${domain}:${sitePath}`)
          .get();
        
        // Removed console.log
        
        // Now use the site ID to get the lists
        try {
          const listsResponse = await graphClient
            .api(`/sites/${siteResponse.id}/lists`)
            .get();
          
          if (listsResponse.value && listsResponse.value.length > 0) {
            // Removed console.log statement about available lists
            
            // Find the list by display name
            const targetList = listsResponse.value.find((list: SharePointList) => 
              list.displayName === listName || list.name === listName);
            
            if (targetList) {
              // Removed console.log
              
              // First, try to get the list columns to understand the field structure
              try {
                const columnsResponse = await graphClient
                  .api(`/sites/${siteResponse.id}/lists/${targetList.id}/columns`)
                  .get();
                
                // Removed console.log statement about list columns
                
                // Create a more targeted list item based on the actual column names
                const refinedFields: Record<string, any> = {
                  // Title is always required
                  Title: data.name || 'New Waitlist Entry'
                };
                
                // Map our data to the actual column names
                columnsResponse.value.forEach((column: any) => {
                  const columnName = column.name;
                  const columnDisplayName = column.displayName;
                  
                  // Map our form data to the appropriate columns
                  if (/name/i.test(columnName) || /name/i.test(columnDisplayName)) {
                    refinedFields[columnName] = data.name;
                  }
                  else if (/email/i.test(columnName) || /email/i.test(columnDisplayName)) {
                    refinedFields[columnName] = data.email;
                  }
                  else if (/phone/i.test(columnName) || /phone/i.test(columnDisplayName)) {
                    // Check if the column is a number type (based on the column properties)
                    const isNumberColumn = column.number !== undefined;
                    
                    if (isNumberColumn && data.phone) {
                      // If it's a number column, remove all non-numeric characters and convert to number
                      const numericPhone = data.phone.replace(/\D/g, '');
                      refinedFields[columnName] = numericPhone ? Number(numericPhone) : null;
                    } else {
                      // Otherwise keep it as a string
                      refinedFields[columnName] = data.phone || '';
                    }
                  }
                  else if (/refer/i.test(columnName) || /refer/i.test(columnDisplayName)) {
                    refinedFields[columnName] = data.referredBy || '';
                  }
                  else if (/consent/i.test(columnName) || /consent/i.test(columnDisplayName) || /opt.?in/i.test(columnName) || /opt.?in/i.test(columnDisplayName)) {
                    refinedFields[columnName] = data.consentToContact || false;
                  }
                  else if (/platform/i.test(columnName) || /platform/i.test(columnDisplayName)) {
                    refinedFields[columnName] = data.platformType || 'Vynterface';
                  }
                });
                
                // Removed console.log
                
                // Use the refined fields if we found any matches
                if (Object.keys(refinedFields).length > 1) { // More than just Title
                  // Create a new list item with the refined fields
                  const refinedListItem = {
                    fields: refinedFields
                  };
                  
                  try {
                    // Use the refined list item instead
                    await graphClient
                      .api(`/sites/${siteResponse.id}/lists/${targetList.id}/items`)
                      .post(refinedListItem);
                      
                    // Removed console.log
                    return true;
                  } catch (postError) {
                    // Removed console.error
                    // Continue to try with original fields
                  }
                }
              } catch (columnsError) {
                // Removed console.warn
              }
              
              // Add the item to the SharePoint list using the site ID and list ID
              // This will only run if we didn't use refined fields
              // Removed console.log
              // Removed console.log
              
              const addItemResponse = await graphClient
                .api(`/sites/${siteResponse.id}/lists/${targetList.id}/items`)
                .post(listItem);
              
              // Removed console.log
              // Removed console.log
              return true;
            } else {
              // Removed console.error and console.log statements
              
              // In development mode, simulate success
              const isDevelopment = process.env.NODE_ENV === 'development' && import.meta.env.VITE_DEV_MODE === 'true';
              if (isDevelopment) {
                // Removed console.log
                return true;
              }
              
              throw new Error(`List '${listName}' not found in SharePoint site`);
            }
          } else {
            // Removed console.error
            
            // In development mode, simulate success
            const isDevelopment = process.env.NODE_ENV === 'development' && import.meta.env.VITE_DEV_MODE === 'true';
            if (isDevelopment) {
              // Removed console.log
              return true;
            }
            
            throw new Error('No lists found in the SharePoint site');
          }
        } catch (listsError) {
          // Removed console.error
          
          // In development mode, simulate success
          const isDevelopment = process.env.NODE_ENV === 'development' && import.meta.env.VITE_DEV_MODE === 'true';
          if (isDevelopment) {
            // Removed console.log
            return true;
          }
          
          throw listsError;
        }
      } catch (siteError) {
        // Removed console.error
        
        // Try another approach using hostname:/sites/sitename format
        try {
          const siteName = sitePath.split('/').filter(Boolean)[1]; // e.g., InvyteOut
          // Removed console.log
          
          const altSiteResponse = await graphClient
            .api(`/sites/${domain}:/sites/${siteName}`)
            .get();
          
          // Removed console.log
          
          // Now use the site ID to get the lists
          const listsResponse = await graphClient
            .api(`/sites/${altSiteResponse.id}/lists`)
            .get();
          
          if (listsResponse.value && listsResponse.value.length > 0) {
            // Removed console.log statement about available lists
            
            // Find the list by display name
            const targetList = listsResponse.value.find((list: SharePointList) => 
              list.displayName === listName || list.name === listName);
            
            if (targetList) {
              // Removed console.log
              
              // First, try to get the list columns to understand the field structure
              try {
                const columnsResponse = await graphClient
                  .api(`/sites/${altSiteResponse.id}/lists/${targetList.id}/columns`)
                  .get();
                
                // Removed console.log statement about list columns
                
                // Create a more targeted list item based on the actual column names
                const refinedFields: Record<string, any> = {
                  // Title is always required
                  Title: data.name || 'New Waitlist Entry'
                };
                
                // Map our data to the actual column names
                columnsResponse.value.forEach((column: any) => {
                  const columnName = column.name;
                  const columnDisplayName = column.displayName;
                  
                  // Map our form data to the appropriate columns
                  if (/name/i.test(columnName) || /name/i.test(columnDisplayName)) {
                    refinedFields[columnName] = data.name;
                  }
                  else if (/email/i.test(columnName) || /email/i.test(columnDisplayName)) {
                    refinedFields[columnName] = data.email;
                  }
                  else if (/phone/i.test(columnName) || /phone/i.test(columnDisplayName)) {
                    // Check if the column is a number type (based on the column properties)
                    const isNumberColumn = column.number !== undefined;
                    
                    if (isNumberColumn && data.phone) {
                      // If it's a number column, remove all non-numeric characters and convert to number
                      const numericPhone = data.phone.replace(/\D/g, '');
                      refinedFields[columnName] = numericPhone ? Number(numericPhone) : null;
                    } else {
                      // Otherwise keep it as a string
                      refinedFields[columnName] = data.phone || '';
                    }
                  }
                  else if (/refer/i.test(columnName) || /refer/i.test(columnDisplayName)) {
                    refinedFields[columnName] = data.referredBy || '';
                  }
                  else if (/consent/i.test(columnName) || /consent/i.test(columnDisplayName) || /opt.?in/i.test(columnName) || /opt.?in/i.test(columnDisplayName)) {
                    refinedFields[columnName] = data.consentToContact || false;
                  }
                  else if (/platform/i.test(columnName) || /platform/i.test(columnDisplayName)) {
                    refinedFields[columnName] = data.platformType || 'Vynterface';
                  }
                });
                
                // Removed console.log
                
                // Use the refined fields if we found any matches
                if (Object.keys(refinedFields).length > 1) { // More than just Title
                  // Create a new list item with the refined fields
                  const refinedListItem = {
                    fields: refinedFields
                  };
                  
                  try {
                    // Use the refined list item instead
                    await graphClient
                      .api(`/sites/${altSiteResponse.id}/lists/${targetList.id}/items`)
                      .post(refinedListItem);
                      
                    // Removed console.log
                    return true;
                  } catch (postError) {
                    // Removed console.error
                    // Continue to try with original fields
                  }
                }
              } catch (columnsError) {
                // Removed console.warn
              }
              
              // Add the item to the SharePoint list using the site ID and list ID
              // This will only run if we didn't use refined fields
              await graphClient
                .api(`/sites/${altSiteResponse.id}/lists/${targetList.id}/items`)
                .post(listItem);
                
              // Removed console.log
              return true;
            } else {
              // Removed console.error
              
              // In development mode, simulate success
              const isDevelopment = process.env.NODE_ENV === 'development' && import.meta.env.VITE_DEV_MODE === 'true';
              if (isDevelopment) {
                // Removed console.log
                return true;
              }
              
              throw new Error(`List '${listName}' not found in SharePoint site`);
            }
          } else {
            // Removed console.error
            
            // In development mode, simulate success
            const isDevelopment = process.env.NODE_ENV === 'development' && import.meta.env.VITE_DEV_MODE === 'true';
            if (isDevelopment) {
              // Removed console.log
              return true;
            }
            
            throw new Error('No lists found in the SharePoint site');
          }
        } catch (altSiteError) {
          // Removed console.error
          
          // In development mode, simulate success
          const isDevelopment = process.env.NODE_ENV === 'development' && import.meta.env.VITE_DEV_MODE === 'true';
          if (isDevelopment) {
            // Removed console.log
            return true;
          }
          
          throw altSiteError;
        }
      }
    } catch (error) {
      // Removed console.error
      
      // In development, we'll simulate success
      if (import.meta.env.DEV) {
        // Removed console.log
        return true;
      }
      
      throw error;
    }

    return true;
  } catch (error) {
    // Removed console.error
    return false;
  }
};

/**
 * Gets the SharePoint site and list IDs
 * @param platform The platform name ('invyteout' or 'vynterface')
 * @param accessToken Access token for authentication
 * @returns Promise resolving to site ID and list ID
 */
export const getSharePointSiteAndList = async (
  platform: string,
  accessToken: string
): Promise<{ siteId: string; listId: string }> => {
  try {
    // Create a graph client with the access token
    const graphClient = Client.initWithMiddleware({
      authProvider: {
        getAccessToken: async () => accessToken
      }
    });
    
    // Get the site ID
    const domain = sharepointConfig.siteUrl ? new URL(sharepointConfig.siteUrl).hostname : 'invyteout.sharepoint.com';
    
    // Get the correct site path based on the platform
    const platformConfig = platform.toLowerCase() === 'vynterface' 
      ? sharepointConfig.vynterface 
      : sharepointConfig.invyteout;
    
    const sitePath = platformConfig.sitePath;
    
    // Removed console.log
    
    // Try to get the site
    const siteResponse = await graphClient
      .api(`/sites/${domain}:${sitePath}`)
      .get();
    
    if (!siteResponse || !siteResponse.id) {
      throw new Error(`Site not found for platform: ${platform}`);
    }
    
    // Removed console.log
    
    // Get the lists
    const listsResponse = await graphClient
      .api(`/sites/${siteResponse.id}/lists`)
      .get();
    
    if (!listsResponse || !listsResponse.value || listsResponse.value.length === 0) {
      throw new Error('No lists found in the SharePoint site');
    }
    
    // Find the waitlist list
    const listName = platform.toLowerCase() === 'vynterface'
      ? sharepointConfig.vynterface.listName 
      : sharepointConfig.invyteout.listName;
      
    const targetList = listsResponse.value.find((list: any) => 
      list.displayName === listName || 
      list.name === listName ||
      list.displayName === 'Waitlist' ||
      list.name === 'Waitlist'
    );
    
    if (!targetList) {
      throw new Error(`List not found: ${listName}`);
    }
    
    // Removed console.log
    
    return {
      siteId: siteResponse.id,
      listId: targetList.id
    };
  } catch (error) {
    // Removed console.error
    throw error;
  }
};

/**
 * Updates the consent status for a user in SharePoint
 * @param identifier Email or phone to identify the user
 * @param identifierType Type of identifier (email or phone)
 * @param newConsentValue New consent value (true or false)
 * @param accessToken Optional access token
 * @param platform Which platform's SharePoint list to update ('invyteout' or 'vynterface')
 * @returns Promise resolving to true if successful, false otherwise
 */
export const updateConsentInSharePoint = async (
  identifier: string,
  identifierType: 'email' | 'phone',
  newConsentValue: boolean = false,
  accessToken?: string,
  platform: string = 'invyteout' // Default to invyteout if not specified
): Promise<boolean> => {
  if (!identifier) {
    // Removed console.error
    return false;
  }
  
  // Log the unsubscribe attempt
  // Removed console.log
  
  // Note: We're no longer simulating in development mode, always making real API calls

  try {
    // Get access token if not provided - but this should be passed in from the component
    if (!accessToken) {
      // Removed console.error
      throw new Error('Authentication failed. No access token provided.');
    }
    
    // Normalize platform name to lowercase and ensure it's either 'invyteout' or 'vynterface'
    const normalizedPlatform = platform.toLowerCase();
    const validPlatform = normalizedPlatform === 'vynterface' ? 'vynterface' : 'invyteout';
    
    // Removed console.log
    
    // Get SharePoint site and list info for the specified platform
    const { siteId, listId } = await getSharePointSiteAndList(validPlatform, accessToken);
    
    if (!siteId || !listId) {
      // Removed console.error
      return false;
    }

    // Build the filter based on identifier type
    let filterQuery = '';
    if (identifierType === 'email') {
      // Normalize the email to lowercase for comparison
      const normalizedEmail = identifier.toLowerCase();
      
      // Check multiple possible field names for email
      // SharePoint field names might vary (Email, UserEmail, EmailAddress, etc.)
      // We need to use the fields/ prefix for each field and make sure the syntax is correct
      filterQuery = `fields/Email eq '${normalizedEmail}'`;
      
      // Removed console.log
      // Removed console.log
    } else if (identifierType === 'phone') {
      // Phone might be stored in different formats, so we use contains
      filterQuery = `contains(fields/Phone, '${identifier}')`;
    }

    // Find the item in SharePoint
    const graphClient = Client.initWithMiddleware({
      authProvider: {
        getAccessToken: async () => accessToken
      }
    });
    
    // Removed console.log
    // Removed console.log
    // Removed console.log
    
    let response;
    try {
      // Log the full API URL we're going to call
      const apiUrl = `/sites/${siteId}/lists/${listId}/items`;
      // Removed console.log
      // Removed console.log
      
      // First try with the filter - we need to expand fields to access them
      try {
        // Add the Prefer header to allow non-indexed queries
        response = await graphClient
          .api(apiUrl)
          .header('Prefer', 'HonorNonIndexedQueriesWarningMayFailRandomly')
          .expand('fields')
          .filter(filterQuery)
          .get();
        
        // Removed console.log
      } catch (filterError) {
        // Removed console.error
        // Removed console.log
        response = null;
      }
      
      // If no results with filter, try getting all items and filtering manually
      if (!response || !response.value || response.value.length === 0) {
        // Removed console.log
        
        // Get all items (limited to top 100 for performance)
        const allItemsResponse = await graphClient
          .api(`/sites/${siteId}/lists/${listId}/items?$expand=fields&$top=100`)
          .header('Prefer', 'HonorNonIndexedQueriesWarningMayFailRandomly')
          .get();
        
        // Removed console.log
        
        // Manually filter items
        if (allItemsResponse?.value?.length > 0) {
          const normalizedEmail = identifier.toLowerCase();
          const matchingItems = allItemsResponse.value.filter((item: any) => {
            // Check various possible email fields
            if (!item.fields) return false;
            
            // Try different possible field names for email
            const possibleEmailFields = ['Email', 'UserEmail', 'EmailAddress', 'Title', 'email', 'useremail'];
            
            for (const fieldName of possibleEmailFields) {
              const fieldValue = item.fields[fieldName];
              if (fieldValue && fieldValue.toLowerCase() === normalizedEmail) {
                // Removed console.log
                return true;
              }
            }
            
            return false;
          });
          
          if (matchingItems.length > 0) {
            // Removed console.log
            response = { value: matchingItems };
          } else {
            // Removed console.error
            return false;
          }
        } else {
          // Removed console.error
          return false;
        }
      } else {
        // Removed console.log
      }
    } catch (apiError: unknown) {
      // Removed console.error
      if (typeof apiError === 'object' && apiError !== null) {
        const error = apiError as any;
        if (error.statusCode) {
          // Removed console.error
        }
        if (error.message) {
          // Removed console.error
        }
      }
      return false;
    }

    // Update each matching record
    let success = true;
    for (const item of response.value) {
      try {
        // Check which fields actually exist in the item before updating
        const fieldsToUpdate: Record<string, boolean> = {};
        
        // Get the item fields to see what's available
        const itemDetails = await graphClient
          .api(`/sites/${siteId}/lists/${listId}/items/${item.id}?$expand=fields`)
          .header('Prefer', 'HonorNonIndexedQueriesWarningMayFailRandomly')
          .get();
          
        // Removed console.log
        
        // Update the Consent field which is a Yes/No field in SharePoint
        // For Yes/No fields in SharePoint, we need to use boolean values
        const possibleConsentFields = ['Consent'];
        
        // SharePoint Yes/No fields expect boolean values
        const newValue = newConsentValue;
        
        for (const fieldName of possibleConsentFields) {
          if (itemDetails.fields && fieldName in itemDetails.fields) {
            // Removed console.log
            fieldsToUpdate[fieldName] = newValue;
          }
        }
        
        // If no fields to update, return false
        if (Object.keys(fieldsToUpdate).length === 0) {
          // Removed console.error
          return false;
        }
        
        // Prepare update payload
        const updateFields = {
          fields: fieldsToUpdate
        };
        
        // Removed console.log
        
        try {
          // Update with boolean values for SharePoint Yes/No fields
          await graphClient
            .api(`/sites/${siteId}/lists/${listId}/items/${item.id}`)
            .header('Prefer', 'HonorNonIndexedQueriesWarningMayFailRandomly')
            .update(updateFields);
            
          // Removed console.log
        } catch (updateError) {
          // Removed console.error
          
          // Log detailed error information
          if (typeof updateError === 'object' && updateError !== null) {
            const error = updateError as any;
            if (error.statusCode) {
              // Removed console.error
            }
            if (error.message) {
              // Removed console.error
            }
            if (error.body) {
              // Removed console.error
            }
          }
          
          // Try one more time with a different format
          try {
            // Try with string values as fallback (some SharePoint configurations might expect this)
            const stringFieldsToUpdate: Record<string, string> = {};
            for (const [key] of Object.entries(fieldsToUpdate)) {
              stringFieldsToUpdate[key] = newConsentValue ? 'Yes' : 'No';
            }
            
            const stringUpdateFields = {
              fields: stringFieldsToUpdate
            };
            
            // Removed console.log
            
            await graphClient
              .api(`/sites/${siteId}/lists/${listId}/items/${item.id}`)
              .header('Prefer', 'HonorNonIndexedQueriesWarningMayFailRandomly')
              .update(stringUpdateFields);
              
            // Removed console.log
          } catch (fallbackError) {
            // Removed console.error
            throw updateError; // Throw the original error
          }
        }
      } catch (updateError) {
        // Removed console.error
        success = false;
      }
    }

    return success;
  } catch (error) {
    // Removed console.error
    return false;
  }
};

/**
 * Submits form data to SharePoint
 * @param data Form data to submit
 * @param platformType Platform type (web, mobile, etc.)
 * @param accessToken Optional access token
 * @returns Promise resolving to true if successful, false otherwise
 * @deprecated This function is kept for reference but is not currently used
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const submitToSharePoint = async (data: FormData, platformType: string, accessToken?: string): Promise<boolean> => {
  // This is a placeholder function that would be implemented elsewhere
  // Removed console.log
  return true;
};
